import React from "react";
import {
  LegalPageLayout,
  LegalSection,
  TocItem,
} from "pages/website/LegalPageLayout";

const PRIVACY_TOC: TocItem[] = [
  { id: "information-we-collect", label: "Information We Collect" },
  { id: "how-we-use", label: "How We Use Your Information" },
  { id: "data-sharing", label: "Data Sharing" },
  { id: "storage-security", label: "Data Storage & Security" },
  { id: "data-retention", label: "Data Retention" },
  { id: "your-rights", label: "Your Rights" },
];

const PrivacyPage: React.FC = () => {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      icon="🔒"
      lastUpdated="Friday, 6th of February, 2026"
      intro={
        <>
          YourQcard respects your privacy and is committed to protecting your
          personal data. This Privacy Policy explains how we collect, use,
          store, and protect information when you use our digital business card
          platform.
        </>
      }
      tocItems={PRIVACY_TOC}
      contactEmail="support@yourqcard.com"
      contactLabel="For data requests or questions, contact us at"
    >
      <LegalSection
        id="information-we-collect"
        number={1}
        title="Information We Collect"
      >
        <p>We may collect the following data:</p>
        <ul>
          <li>
            <strong>Personal Information:</strong> Name, job title, company
            name, email address, phone number, profile photo, social links.
          </li>
          <li>
            <strong>Account Information:</strong> Login credentials,
            subscription plan, billing status.
          </li>
          <li>
            <strong>Company Data:</strong> Company profile, employee card
            details (for corporate accounts).
          </li>
          <li>
            <strong>Usage Data:</strong> Device type, browser, IP address,
            interaction logs.
          </li>
          <li>
            <strong>Payment Data:</strong> Managed securely by third-party
            payment providers (we do not store card details).
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="how-we-use" number={2} title="How We Use Your Information">
        <p>We use your data to:</p>
        <ul>
          <li>Create and manage digital business cards</li>
          <li>Enable card sharing via QR, NFC, or links</li>
          <li>Manage subscriptions and billing</li>
          <li>Provide customer support</li>
          <li>Improve platform performance and features</li>
          <li>Ensure security and prevent misuse</li>
        </ul>
      </LegalSection>

      <LegalSection id="data-sharing" number={3} title="Data Sharing">
        <p>We may share limited data with:</p>
        <ul>
          <li>Hosting and cloud service providers</li>
          <li>Payment processors</li>
          <li>Analytics tools (aggregated & anonymized)</li>
          <li>Legal authorities if required by law</li>
        </ul>
      </LegalSection>

      <LegalSection
        id="storage-security"
        number={4}
        title="Data Storage & Security"
      >
        <ul>
          <li>Data is stored on secure servers</li>
          <li>Industry-standard security measures are applied</li>
          <li>Access is restricted to authorized personnel only</li>
        </ul>
      </LegalSection>

      <LegalSection id="data-retention" number={5} title="Data Retention">
        <ul>
          <li>Data is retained as long as your account is active</li>
          <li>
            Upon account deletion, data is permanently removed unless legally
            required otherwise
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="your-rights" number={6} title="Your Rights">
        <p>You have the right to:</p>
        <ul>
          <li>Access your data</li>
          <li>Update or correct information</li>
          <li>Request deletion of your account and data</li>
          <li>Withdraw consent for marketing communications</li>
        </ul>
      </LegalSection>
    </LegalPageLayout>
  );
};

export default PrivacyPage;
